import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import type { Action, Resource } from '@/context/permissions';

interface WithPermissionProps {
    resource: Resource;
    action: Action;
    children: React.ReactElement;
    /** Rendered when the user lacks permission. Defaults to null (hidden). */
    fallback?: React.ReactNode;
}

/**
 * WithPermission
 * --------------
 * Conditionally renders `children` when the current user has the
 * specified resource + action permission. Otherwise renders `fallback`.
 *
 * Admin role always renders children — bypasses all permission checks.
 * Mirrors the nassfoods_admin ProtectedRoute Admin bypass pattern.
 *
 * Usage in JSX:
 *   <WithPermission resource="product" action="create">
 *       <Button>Add Product</Button>
 *   </WithPermission>
 */
const WithPermission: React.FC<WithPermissionProps> = ({
    resource,
    action,
    children,
    fallback = null,
}) => {
    const { isAdmin, hasPermission, isLoading } = usePermissions();

    // // 1. If still loading user/permissions, return null to avoid flicker
    // if (isLoading) {
    //     return null;
    // }

    // 2. Admin or specific permission granted
    if (isAdmin || hasPermission(resource, action)) {
        return children;
    }

    // 3. Permission denied
    return <>{fallback}</>;
};

export default WithPermission;
